import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  TextField,
  Button,
  Chip,
  Stack,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  Checkbox,
  CircularProgress,
  Paper,
  Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useAuth } from '../../../contexts/AuthContext';
import { Device, DeviceSetting, InferenceModel, InferenceModelClass } from '../types';

interface DeviceSmsTabProps {
  deviceId: string;
}

const AU_MOBILE_REGEX = /^(\+?61|0)4\d{8}$/;

const SETTING_ENABLED = 'device.sms.enabled';
const SETTING_NUMBER = 'device.sms.number';
const SETTING_MIN = 'device.sms.min';
const SETTING_CLASSES = 'device.sms.classes';
const SETTING_COOLDOWN = 'device.sms.sendnotice-every-x-minutes';

// device.sms.classes is a JSON object mapping class name -> per-class minimum
// count, e.g. {"cat":3,"pig":1}. Older configs stored a plain comma-separated
// list of class names with no threshold — treat those as a minimum of 1 each.
const parseClassThresholds = (raw: string): Map<string, string> => {
  const result = new Map<string, string>();
  if (!raw) return result;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      for (const [key, value] of Object.entries(parsed)) {
        result.set(key, String(value));
      }
      return result;
    }
  } catch {
    // Fall through to legacy CSV parsing below.
  }
  raw.split(',').map((c) => c.trim()).filter(Boolean).forEach((c) => result.set(c, '1'));
  return result;
};

const DeviceSmsTab: React.FC<DeviceSmsTabProps> = ({ deviceId }) => {
  const auth = useAuth();
  const accessToken = auth.tokens?.accessToken;
  const authHeader = useMemo(() => ({ Authorization: `Bearer ${accessToken}` }), [accessToken]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [smsOwner, setSmsOwner] = useState<Device | null>(null);
  const [isOnParent, setIsOnParent] = useState(false);
  const [existingSettings, setExistingSettings] = useState<DeviceSetting[]>([]);
  const [availableClasses, setAvailableClasses] = useState<InferenceModelClass[]>([]);

  const [enabled, setEnabled] = useState(true);
  const [numbers, setNumbers] = useState<string[]>([]);
  const [numberInput, setNumberInput] = useState('');
  const [numberError, setNumberError] = useState<string | null>(null);
  const [classFilterMode, setClassFilterMode] = useState<'any' | 'selected'>('any');
  const [classThresholds, setClassThresholds] = useState<Map<string, string>>(new Map());
  const [minCount, setMinCount] = useState('1');
  const [cooldownMinutes, setCooldownMinutes] = useState('60');

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const deviceRes = await fetch(`/api/devices/${deviceId}`, { headers: authHeader });
      if (!deviceRes.ok) throw new Error('Failed to load device');
      const device: Device = await deviceRes.json();

      const ownerId = device.parentId || deviceId;
      const onParent = Boolean(device.parentId);
      setIsOnParent(onParent);

      const ownerDevice = onParent
        ? await (await fetch(`/api/devices/${ownerId}`, { headers: authHeader })).json()
        : device;
      setSmsOwner(ownerDevice);

      const settingsRes = await fetch(`/api/devices/${ownerId}/settings`, { headers: authHeader });
      if (!settingsRes.ok) throw new Error('Failed to load device settings');
      const settings: DeviceSetting[] = await settingsRes.json();
      setExistingSettings(settings);

      const byName = new Map(settings.map((s) => [s.settingName, s.settingValue]));
      setEnabled(byName.get(SETTING_ENABLED) !== 'false');
      const rawNumbers = byName.get(SETTING_NUMBER) || '';
      setNumbers(rawNumbers.split(',').map((n) => n.trim()).filter(Boolean));
      setMinCount(byName.get(SETTING_MIN) || '1');
      setCooldownMinutes(byName.get(SETTING_COOLDOWN) || '60');
      const thresholds = parseClassThresholds(byName.get(SETTING_CLASSES) || '');
      if (thresholds.size > 0) {
        setClassFilterMode('selected');
        setClassThresholds(thresholds);
      } else {
        setClassFilterMode('any');
        setClassThresholds(new Map());
      }

      // Union of classes from the device's own model and (if applicable) its
      // parent's model — mirrors how SmsService resolves detection class names.
      const modelIds = [device.inferenceModelId, onParent ? ownerDevice.inferenceModelId : null]
        .filter((v): v is string => Boolean(v));
      const uniqueModelIds = Array.from(new Set(modelIds));
      const classesByName = new Map<string, InferenceModelClass>();
      for (const modelId of uniqueModelIds) {
        const modelRes = await fetch(`/api/inference-models/${modelId}`, { headers: authHeader });
        if (modelRes.ok) {
          const model: InferenceModel = await modelRes.json();
          for (const cls of model.classes || []) {
            classesByName.set(cls.className, cls);
          }
        }
      }
      setAvailableClasses(Array.from(classesByName.values()).sort((a, b) => a.className.localeCompare(b.className)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNumber = () => {
    const cleaned = numberInput.replace(/\s|-/g, '');
    if (!AU_MOBILE_REGEX.test(cleaned)) {
      setNumberError('Enter a valid Australian mobile number (e.g. 0412 345 678)');
      return;
    }
    if (numbers.includes(cleaned)) {
      setNumberError('That number is already added');
      return;
    }
    setNumbers((prev) => [...prev, cleaned]);
    setNumberInput('');
    setNumberError(null);
  };

  const handleRemoveNumber = (number: string) => {
    setNumbers((prev) => prev.filter((n) => n !== number));
  };

  const toggleClass = (className: string) => {
    setClassThresholds((prev) => {
      const next = new Map(prev);
      if (next.has(className)) next.delete(className);
      else next.set(className, '1');
      return next;
    });
  };

  const setClassThreshold = (className: string, value: string) => {
    setClassThresholds((prev) => {
      const next = new Map(prev);
      next.set(className, value);
      return next;
    });
  };

  const upsertSetting = async (ownerId: string, name: string, value: string) => {
    const existing = existingSettings.find((s) => s.settingName === name);
    if (!value) {
      // Clearing a setting that doesn't exist yet is a no-op.
      if (!existing) return;
      await fetch(`/api/devices/${ownerId}/settings/${existing.id}`, {
        method: 'DELETE',
        headers: authHeader,
      });
      return;
    }
    if (existing) {
      if (existing.settingValue === value) return;
      await fetch(`/api/devices/${ownerId}/settings/${existing.id}`, {
        method: 'PUT',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: existing.id, deviceId: ownerId, settingName: name, settingValue: value }),
      });
    } else {
      await fetch(`/api/devices/${ownerId}/settings`, {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ settingName: name, settingValue: value }),
      });
    }
  };

  const handleSave = async () => {
    if (!smsOwner?.id) return;
    setSaving(true);
    setSaveSuccess(false);
    setError(null);
    try {
      const ownerId = smsOwner.id;
      const classesValue = classFilterMode === 'selected' && classThresholds.size > 0
        ? JSON.stringify(Object.fromEntries(
            Array.from(classThresholds.entries()).map(([name, count]) => [name, Number(count) || 1])
          ))
        : '';
      await Promise.all([
        upsertSetting(ownerId, SETTING_ENABLED, enabled ? 'true' : 'false'),
        upsertSetting(ownerId, SETTING_NUMBER, numbers.join(',')),
        upsertSetting(ownerId, SETTING_MIN, minCount || '1'),
        upsertSetting(ownerId, SETTING_COOLDOWN, cooldownMinutes || '60'),
        upsertSetting(ownerId, SETTING_CLASSES, classesValue),
      ]);
      setSaveSuccess(true);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save SMS settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="h6">SMS Alerts</Typography>
        <FormControlLabel
          control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
          label={enabled ? 'Enabled' : 'Disabled'}
          labelPlacement="start"
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Send a text message when this device detects something. Requires an Inference Model to be
        assigned under Settings.
      </Typography>

      {isOnParent && smsOwner && (
        <Alert severity="info" sx={{ mb: 2 }}>
          These settings apply to the whole camera group and are shared with — and configured on —
          the parent device, <strong>{smsOwner.name}</strong>.
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {saveSuccess && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaveSuccess(false)}>SMS settings saved.</Alert>}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Phone Numbers</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          {numbers.map((n) => (
            <Chip key={n} label={n} onDelete={() => handleRemoveNumber(n)} size="small" />
          ))}
          {numbers.length === 0 && (
            <Typography variant="body2" color="text.secondary">No numbers configured yet.</Typography>
          )}
        </Stack>
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            placeholder="0412 345 678"
            value={numberInput}
            onChange={(e) => { setNumberInput(e.target.value); setNumberError(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddNumber(); } }}
            error={!!numberError}
            helperText={numberError}
            fullWidth
          />
          <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddNumber}>
            Add
          </Button>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Trigger</Typography>
        <FormControl component="fieldset" sx={{ mb: 1 }}>
          <FormLabel component="legend">Classes</FormLabel>
          <RadioGroup
            row
            value={classFilterMode}
            onChange={(e) => setClassFilterMode(e.target.value as 'any' | 'selected')}
          >
            <FormControlLabel value="any" control={<Radio size="small" />} label="Any detected class" />
            <FormControlLabel value="selected" control={<Radio size="small" />} label="Only selected classes" />
          </RadioGroup>
        </FormControl>

        {classFilterMode === 'any' ? (
          <TextField
            label="Minimum detections to trigger an SMS"
            type="number"
            size="small"
            value={minCount}
            onChange={(e) => setMinCount(e.target.value)}
            inputProps={{ min: 1 }}
            fullWidth
          />
        ) : availableClasses.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No Inference Model classes available — assign a model to this device under Settings first.
          </Typography>
        ) : (
          <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary">
              Check a class and set the minimum detection count that should trigger an SMS for it.
            </Typography>
            {availableClasses.map((cls) => {
              const checked = classThresholds.has(cls.className);
              return (
                <Stack key={cls.id} direction="row" spacing={1} alignItems="center">
                  <FormControlLabel
                    sx={{ minWidth: 140, flexGrow: 1 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={checked}
                        onChange={() => toggleClass(cls.className)}
                      />
                    }
                    label={cls.className}
                  />
                  <TextField
                    label="Min"
                    type="number"
                    size="small"
                    disabled={!checked}
                    value={classThresholds.get(cls.className) ?? ''}
                    onChange={(e) => setClassThreshold(cls.className, e.target.value)}
                    inputProps={{ min: 1 }}
                    sx={{ width: 90 }}
                  />
                </Stack>
              );
            })}
          </Stack>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Cooldown</Typography>
        <TextField
          label="Minutes between SMS alerts for this device"
          type="number"
          size="small"
          value={cooldownMinutes}
          onChange={(e) => setCooldownMinutes(e.target.value)}
          inputProps={{ min: 1 }}
          helperText="Prevents repeated alerts firing back-to-back."
          fullWidth
        />
      </Paper>

      <Button
        variant="contained"
        onClick={handleSave}
        disabled={saving || (classFilterMode === 'selected' && classThresholds.size === 0)}
      >
        {saving ? 'Saving…' : 'Save SMS Settings'}
      </Button>
    </Box>
  );
};

export default DeviceSmsTab;
